import React, { FC, useEffect, useState, useMemo } from 'react';
import { CircularProgress, useMediaQuery } from '@mui/material';
import {
  Grid as KendoGrid,
  GridColumn,
  GridNoRecords,
  GridDetailExpandChangeEvent, // Changed from GridExpandChangeEvent
  GridCellProps,
  GridPageChangeEvent,
  GridDataStateChangeEvent,
} from '@progress/kendo-react-grid';
import { CompositeFilterDescriptor, FilterDescriptor, State as ODataState } from '@progress/kendo-data-query';
import customerApi from 'src/api/ActiveCustomerApi';
import useAuth from 'src/hooks/useAuth';
import { useSearchParams } from 'react-router-dom';
import ColumnMenu from '../grid/ColumnMenu';
import CustomerContactsDetail from './CustomerContactsDetail';
import PhoneNumberCell from '../grid/PhoneNumberCell';
import EmailCell from '../grid/EmailCell';
import useSettings from 'src/hooks/useSettings';
import { SETTING_KEYS } from 'src/constants';
import { DetailExpandDescriptor } from '@progress/kendo-react-data-tools'; // Assuming this is the correct import path based on previous successful usage
import { getPageSizeSetting } from 'src/utils/settingsHelper';

const CustomerContacts: FC<React.PropsWithChildren<unknown>> = () => {
  const CustomerContactsPageSizeKey = SETTING_KEYS.CustomerContactsPageSize;
  const isLg = useMediaQuery('(min-width:960px)');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [customers, setCustomers] = useState<any[]>([]);
  const { user } = useAuth();
  const [total, setTotal] = useState(0);
  const SalesRepId: string = user?.salesReps && user?.salesReps.length > 0 ? user.salesReps[0] : '';
  const [search] = useSearchParams();

  const savePagingSettings = useSettings((state) => state.savePagingSettings);
  const userSettings = useSettings((state) => state.userSettings);

  const initialPageSize = getPageSizeSetting(userSettings, CustomerContactsPageSizeKey, 50);

  const [defaultFilter] = useState<CompositeFilterDescriptor | FilterDescriptor>({
    filters: [
      { field: 'hardGoodsRepCode', operator: 'eq', value: SalesRepId },
      { field: 'lawnGardenRepCode', operator: 'eq', value: SalesRepId },
      { field: 'plantRepCode', operator: 'eq', value: SalesRepId },
      { field: 'carrRepCode', operator: 'eq', value: SalesRepId },
    ],
    logic: 'or',
  });

  const [gridDataState, setGridDataState] = useState<ODataState>({
    filter: {
      filters: SalesRepId ? [defaultFilter] : [], // Apply default filter only if SalesRepId is present
      logic: 'and',
    },
    skip: 0,
    take: initialPageSize,
  });

  // State for controlled detail row expansion
  const [expandedDetailState, setExpandedDetailState] = useState<DetailExpandDescriptor>({});
  const dataItemKey = 'number'; // Define dataItemKey, assuming 'number' is the unique ID field

  const loadCustomers = (currentState: ODataState) => {
    setIsLoading(true);
    customerApi
      .getCustomers(currentState)
      .then((response) => {
        // Remove 'expanded: false' initialization as it's handled by expandedDetailState
        setCustomers(response.value || []);
        setTotal(response['@odata.count'] || 0);
        setIsLoading(false);
      })
      .catch(() => {
        setIsLoading(false);
        setCustomers([]);
        setTotal(0);
      });
  };

  // New handler for detail row expansion state changes
  const handleDetailExpandChange = (event: GridDetailExpandChangeEvent) => {
    setExpandedDetailState(event.detailExpand);
  };

  useEffect(() => {
    const filters: (CompositeFilterDescriptor | FilterDescriptor)[] = [];
    if (search.get('allcustomers') !== 'true' && SalesRepId) {
      filters.push(defaultFilter);
    }
    // Prevent unnecessary state update if filters haven't changed
    setGridDataState((prevState) => {
      const newFilterState = { filters: filters, logic: 'and' as const };
      if (JSON.stringify(prevState.filter) === JSON.stringify(newFilterState) && prevState.skip === 0) {
        return prevState;
      }
      return {
        ...prevState,
        filter: newFilterState,
        skip: 0,
      };
    });
  }, [search, SalesRepId, defaultFilter]); // Removed gridDataState from dependencies

  useEffect(() => {
    loadCustomers(gridDataState);
  }, [gridDataState]);

  const handleGridDataStateChange = (e: GridDataStateChangeEvent) => {
    setGridDataState(e.dataState);
  };

  const handlePageChange = (e: GridPageChangeEvent) => {
    const newPageSize = e.page.take;
    setGridDataState((prevState) => ({
      ...prevState,
      skip: e.page.skip,
      take: newPageSize,
    }));
    savePagingSettings(newPageSize, CustomerContactsPageSizeKey);
  };

  const RenderEmailCell = (props: GridCellProps) => <EmailCell {...props} linked />;
  const RenderPhoneNumberCell = (props: GridCellProps) => <PhoneNumberCell {...props} linked />;

  return (
    <KendoGrid
      data={customers}
      skip={gridDataState.skip}
      take={gridDataState.take}
      sort={gridDataState.sort}
      filter={gridDataState.filter}
      pageable={{
        buttonCount: 4,
        pageSizes: [20, 50, 100, 500],
        pageSizeValue: gridDataState.take, // Corrected from pageSizeValue
      }}
      onPageChange={handlePageChange}
      total={total}
      onDataStateChange={handleGridDataStateChange}
      dataItemKey={dataItemKey} // ADDED: Crucial for detailExpand to work correctly
      detail={CustomerContactsDetail}
      detailExpand={expandedDetailState} // CHANGED: Use new state for expansion
      onDetailExpandChange={handleDetailExpandChange} // CHANGED: Use new handler and event
      // expandField="expanded" // REMOVED: Deprecated
      // onExpandChange={expandChange} // REMOVED: Old handler

      columnMenu={ColumnMenu}
    >
      <GridNoRecords>{isLoading ? <CircularProgress /> : 'No records available.'}</GridNoRecords>

      <GridColumn title="Customer" field="name" width={300}></GridColumn>
      <GridColumn title="Customer Email" field="email" width={300} cells={{ data: RenderEmailCell }}></GridColumn>
      <GridColumn title="Customer Phone" field="customerPhoneNumber" width={isLg ? 280 : 160} cells={{ data: RenderPhoneNumberCell }}></GridColumn>
      <GridColumn title="Credit Manager" field="creditManager" width={isLg ? 280 : 200}></GridColumn>
    </KendoGrid>
  );
};

export default CustomerContacts;
